import { memo, ReactNode } from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import { generatePath, Link } from 'react-router-dom'

import { MemberProfile } from 'types'
import { GROUP_PATH } from 'paths'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import Styles from './ProfileInfoTable.module.scss'
import { enUsData, localeData } from '../Form/localeData'
import { LocalisedCurrentTime } from '../LocalisedCurrentTime/LocalisedCurrentTime'

dayjs.extend(advancedFormat)

interface Props {
  profile: MemberProfile
}

const Row = ({ heading, content, topAlign }: { heading: string; content: string | ReactNode; topAlign?: boolean }) => (
  <tr className={cx(Styles.row, { [Styles.topAlign]: topAlign })}>
    <th className={Styles.rowHeading}>{heading}</th>
    <td className={Styles.rowContent}>{content}</td>
  </tr>
)

const ProfileInfoTable = ({ profile }: Props) => {
  const { location, phone, email, timezone, birthday, groups } = profile
  const dateTimeOptions = Intl.DateTimeFormat().resolvedOptions()
  const localData = localeData.get(dateTimeOptions.locale) || enUsData

  return (
    <table className={Styles.table}>
      <colgroup>
        <col className={Styles.headerColumn} />
        <col className={Styles.contentColumn} />
      </colgroup>
      <tbody>
        {location && <Row heading="Location" content={location} />}
        {phone && (
          <Row
            heading="Phone"
            content={
              <a href={`tel:${phone}`} title={phone}>
                {phone}
              </a>
            }
          />
        )}
        {email && (
          <Row
            heading="Email"
            content={
              <a href={`mailto:${email}`} title={email}>
                {email}
              </a>
            }
          />
        )}
        {timezone && <Row heading="Local Timezone" content={<LocalisedCurrentTime timezone={timezone} />} />}
        {birthday && <Row heading="Birthday" content={dayjs(birthday).format(localData.advancedFormat)} />}
        {!!groups?.length && (
          <Row
            heading="Groups"
            topAlign
            content={
              <>
                {groups.map((group) => (
                  <div key={group.id} className={Styles.groupRow}>
                    <Link to={generatePath(GROUP_PATH, { workspaceId: group.workspace_id, id: group.id })}>
                      {group.name}
                    </Link>
                  </div>
                ))}
              </>
            }
          />
        )}
      </tbody>
    </table>
  )
}

const arePropsEqual = (prevProps: Props, nextProps: Props) => JSON.stringify(prevProps) === JSON.stringify(nextProps)

const MemoizedTable = memo(ProfileInfoTable, arePropsEqual)

export { MemoizedTable as ProfileInfoTable }
